import React, { useReducer, useEffect } from "react";
import AuthContext from "./AuthContext";
import AuthReducer from "./AuthReducer";

import clienteAxios from "../../config/axios";
import tokenAuth from "../../config/tokenAuth";
import jwt from "jwt-decode";

import { OBTENER_USUARIO, LOGIN_EXITOSO, LOGIN_ERROR, CERRAR_SESION, TOKEN_EXPIRADO } from "../../types";

export default function AuthState(props) {
  const initialState = {
    token: localStorage.getItem("token"),
    autenticado: null,
    usuario: null,
    mensaje: null,
    cargando: true,
  };

  const [state, dispatch] = useReducer(AuthReducer, initialState);

  const esErrorConexion = (error) => {
    return !navigator.onLine || !error.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
  };

  const respuestaErrorConexion = () => ({
    data: {
      message: "Fallo en la conexión al servidor",
      error: "hubo error",
    },
  });

  const usuarioAutenticado = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      dispatch({
        type: LOGIN_ERROR,
      });
      return null;
    }

    try {
      const decodeToken = jwt(token);
      const dateNow = new Date();

      if (decodeToken.exp * 1000 < dateNow.getTime()) {
        tokenAuth();

        dispatch({
          type: TOKEN_EXPIRADO,
          payload: "Token expirado",
        });
        return null;
      }

      tokenAuth(token);

      const res = await clienteAxios.get("/obtener/usuario", {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Cache-Control": "no-store",
        },
      });

      res.data.authorities = decodeToken.authorities;

      dispatch({
        type: OBTENER_USUARIO,
        payload: res.data,
      });

      return res;
    } catch (error) {
      if (esErrorConexion(error)) {
        return respuestaErrorConexion();
      }

      dispatch({
        type: LOGIN_ERROR,
        payload: null,
      });

      return error.response || null;
    }
  };

  const iniciarSesion = async (datos) => {
    try {
      const res = await clienteAxios.post("/login", datos, {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Cache-Control": "no-store",
        },
      });

      dispatch({
        type: LOGIN_EXITOSO,
        payload: res.data,
      });

      await usuarioAutenticado();
      return res;
    } catch (error) {
      dispatch({
        type: LOGIN_ERROR,
        payload: null,
      });

      if (!error.response) {
        error.response = respuestaErrorConexion();
      }

      return error.response;
    }
  };

  const cerrarSesion = () => {
    dispatch({
      type: CERRAR_SESION,
    });
  };

  const checkReCaptchaToken = async (value) => {
    try {
      const res = await clienteAxios.post(
        "https://www.google.com/recaptcha/api/siteverify",
        { secret: state.key, response: value },
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Cache-Control": "no-store",
          },
        }
      );

      return res;
    } catch (error) {
      if (!error.response) {
        error.response = respuestaErrorConexion();
      }

      return error.response;
    }
  };

  useEffect(() => {
    const syncAuthAcrossTabs = (e) => {
      if (e.key !== "token") return;

      const nuevoToken = e.newValue;

      if (!nuevoToken) {
        cerrarSesion();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      } else {
        usuarioAutenticado();
        if (window.location.pathname === "/login") {
          window.location.href = "/";
        }
      }
    };

    window.addEventListener("storage", syncAuthAcrossTabs);
    return () => window.removeEventListener("storage", syncAuthAcrossTabs);
  }, []);

  useEffect(() => {
    const handleAuthCheck = () => {
      const token = localStorage.getItem("token");
      if (!token) {
        cerrarSesion();
        if (window.location.pathname !== "/login") window.location.href = "/login";
        return;
      }
      try {
        const decoded = jwt(token);
        const now = Date.now();
        if ((decoded.exp || 0) * 1000 <= now) {
          cerrarSesion();
          if (window.location.pathname !== "/login") window.location.href = "/login";
        } else {
          usuarioAutenticado();
          if (window.location.pathname === "/login") window.location.href = "/";
        }
      } catch {
        cerrarSesion();
        if (window.location.pathname !== "/login") window.location.href = "/login";
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") handleAuthCheck();
    };

    window.addEventListener("focus", handleAuthCheck);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", handleAuthCheck);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        autenticado: state.autenticado,
        usuario: state.usuario,
        mensaje: state.mensaje,
        cargando: state.cargando,
        iniciarSesion,
        usuarioAutenticado,
        cerrarSesion,
        checkReCaptchaToken,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}
